import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGetAllsidemenu, useGetSettings, useUpdateSettings, useUpdateUserLanguage } from "../hooks/hooks";
import { changeLanguage } from "../components/common";
import { useDispatch } from "react-redux";
import { fetchSettings } from "../hooks/settingsSlice";
import { useSelector } from 'react-redux';
import { fetchSidebarMenu } from '../API/settings';
import { showErrorToast, showSuccessToast } from '../components/common';
import { useLoader } from "../hooks/LoaderContext";
import { SettingIcon } from "../assets/icons/icon";
import Header from "../components/Common/Header";

const Setting = () => {
  const { i18n, t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [userId, setUserId] = useState(null);
  const [catererId, setCatererId] = useState(null);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const { setLoading } = useLoader();

  // const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [menuProtection, setMenuProtection] = useState(false);
  const [protectionPassword, setProtectionPassword] = useState('');
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { mutate: updateLanguage, isLoading } = useUpdateUserLanguage();

  const [notifications, setNotifications] = useState({ email: false, sms: false });
  const { mutate: saveSettings, isLoading: isSaving } = useUpdateSettings();
  // const { data: allSideMenu, isLoading: isAllSideMenuLoading } = useGetAllsidemenu();
  const { data: settingsData, isLoading: isSettingsLoading } = useGetSettings(catererId);

  const { sidebarMenu, isLoading: isAllSideMenuLoading, error: sidebarError } = useSelector((state) => state.sidebar);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (settingsData) {
      if (settingsData.notifications) {
        setNotifications(settingsData.notifications);
      }
      if (settingsData.theme) {
        setDarkMode(settingsData.theme === "dark");
      }
      if (settingsData.menuProtection) {
        setMenuProtection(settingsData.menuProtection.enabled);
        setProtectionPassword(settingsData.menuProtection.password || "");
        setSelectedMenus((settingsData.menuProtection.protectedMenus || []).map(menu => menu._id || menu));
      }
    }
  }, [settingsData]);

  useEffect(() => {
    const userData = localStorage.getItem("CurrentUser");
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUserId(parsedUser?.id || parsedUser?._id);
      setCatererId(parsedUser?.catererDetails?._id || parsedUser?.catererDetails?.id || parsedUser?.catererId);
    }
  }, []);

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    setIsDropdownOpen(false);

    updateLanguage(
      { userId, preferredLanguage: language },
      {
        onSuccess: (res) => {
          // Use the common utility function for consistent language handling
          changeLanguage(language);
          dispatch(fetchSettings(catererId));
        },
        onError: (err) => {
          console.error("Error updating language:", err);
        }
      }
    );
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSave = () => {
    if (!catererId) return;

    if (menuProtection == true && protectionPassword == "") {
      showErrorToast("Please enter password");
      return;
    }

    if (menuProtection == true && selectedMenus.length === 0) {
      showErrorToast("Please select at least one menu to protect");
      return;
    }
    setLoading(true);

    saveSettings({
      catererId,
      theme: darkMode ? "dark" : "light",
      menuProtection: {
        enabled: menuProtection,
        password: protectionPassword,
        protectedMenus: selectedMenus,
      },
      notifications,
      updatedBy: userId,
    },
      {
        onSuccess: () => {
          showSuccessToast("Settings saved successfully.");
          dispatch(fetchSettings(catererId));
          dispatch(fetchSidebarMenu());
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        },
        onError: () => {
          //  showErrorToast("Failed to save settings. Please try again.");
          setLoading(false);
        }
      }
    );
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="">
        {/* Header Section */}
        <Header title={t('settings')} subTitle={t('manage_your_application_preferences')} pageIcon={<SettingIcon />} className="mb-4" titleClass="text-2xl mb-0" />

        {/* Menu Protection Settings - Full Width */}
        <div className="grid grid-cols-1 gap-6 mb-6 z-10">
          <div className="bg-gradient-to-br from-slate-50 via-orange-50/20 to-amber-50/30 rounded-2xl p-6 shadow-sm border border-slate-200/60 hover:shadow-lg hover:border-slate-300/80 transition-all duration-500 backdrop-blur-sm relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{t('menu_protection')}</h3>
                <p className="text-sm text-slate-600">{t('menu_protection_description')}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div>
                  <h4 className="text-sm font-medium text-slate-800">{t('enable_menu_protection')}</h4>
                  <p className="text-xs text-slate-600">{t('enable_menu_protection_description')}</p>
                </div>
                <button
                  onClick={() => setMenuProtection(!menuProtection)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500/20 ${menuProtection ? 'bg-gradient-to-r from-orange-500 to-red-600 shadow-md' : 'bg-slate-200 hover:bg-slate-300'
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 shadow-sm ${menuProtection ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>

              {/* Additional Info */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-xs text-orange-800">
                    <p className="font-medium mb-1">{t("what_happens")}</p>
                    <ul className="space-y-1 text-orange-700">
                      <li>• {t("points.popup")}</li>
                      <li>• {t("points.enter_password")}</li>
                      <li>• {t("points.blocked")}</li>
                      <li>• {t("points.logged")}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Additional Configuration when enabled */}
              {menuProtection && (
                <div className="space-y-4 pt-4 border-t border-gray-100">
                  {/* Password Input */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('set_protection_password')}
                    </label>
                    <input
                      type="password"
                      value={protectionPassword}
                      onChange={(e) => setProtectionPassword(e.target.value)}
                      placeholder={t('protection_password_placeholder')}
                      className="max-w-md w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {t('set_protection_password_note')}
                    </p>
                  </div>

                  {/* Menu Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('select_menus_to_protect')}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {!isAllSideMenuLoading && sidebarMenu?.length > 0 ? sidebarMenu?.map((menu) => (
                        <label key={menu.id} className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all duration-300 group ${selectedMenus.includes(menu._id)
                          ? 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-300 shadow-md'
                          : 'border-slate-200 hover:bg-gradient-to-r hover:from-orange-50 hover:to-red-50 hover:border-orange-200'
                          }`}>
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={selectedMenus.includes(menu._id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMenus([...selectedMenus, menu._id]);
                                } else {
                                  setSelectedMenus(selectedMenus.filter(id => id !== menu._id));
                                }
                              }}
                              className="sr-only"
                            />
                            <div className={`w-6 h-6 border-2 rounded-lg flex items-center justify-center transition-all duration-300 ${selectedMenus.includes(menu._id)
                              ? 'bg-gradient-to-r from-orange-500 to-red-500 border-orange-500 shadow-lg'
                              : 'border-slate-300 group-hover:border-orange-400'
                              }`}>
                              {selectedMenus.includes(menu._id) && (
                                <svg className="w-4 h-4 text-white transform scale-100 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                          {/* <span className="text-xl group-hover:scale-110 transition-transform duration-300">{menu.icon}</span> */}
                          <span className={`text-sm font-medium transition-colors duration-300 ${selectedMenus.includes(menu._id) ? 'text-orange-700' : 'text-slate-800'
                            }`}>
                            {menu.text[selectedLanguage]}
                          </span>
                          {selectedMenus.includes(menu._id) && (
                            <div className="ml-auto">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 border border-orange-200">
                                Protected
                              </span>
                            </div>
                          )}
                        </label>
                      )) : (
                        <p className="text-xs text-slate-600 mt-2">
                          No menus available
                        </p>
                      )}
                    </div>
                    {selectedMenus.length === 0 && (
                      <p className="text-xs text-orange-600 mt-2">
                        Please select at least one menu to protect
                      </p>
                    )}
                  </div>
                </div>
              )}


              <div className="mt-8 flex gap-4 justify-end">
                <button className="px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
                  onClick={handleSave}>
                  {t("save_settings")}
                </button>
              </div>

            </div>
          </div>
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 z-50">
          {/* Language Settings */}
          <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 rounded-2xl p-6 shadow-sm border border-slate-200/60 hover:shadow-lg hover:border-slate-300/80 transition-all duration-500 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.674c-.29.61-.373 1.25-.42 1.9a21.163 21.163 0 01-.227 4.05 1 1 0 11-2-.005c.007-.378.054-.75.14-1.095.087-.35.214-.675.366-.976A20.275 20.275 0 015.292 7H4a1 1 0 110-2h1V3a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{t("language_region.title")}</h3>
                <p className="text-sm text-slate-600">{t("language_region.description")}</p>
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">
                {t('select_language')}
              </label>

              {/* Custom Dropdown */}
              <div className="relative z-[999]" ref={dropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className="w-full px-4 py-3 pr-10 border border-slate-200 rounded-xl focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 bg-white/80 backdrop-blur-sm text-left cursor-pointer hover:bg-white hover:border-slate-300 hover:shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                      <span className="text-xs font-medium text-white">
                        {selectedLanguage === 'en' ? 'EN' : 'GU'}
                      </span>
                    </div>
                    <span className="text-slate-800 font-medium">
                      {selectedLanguage === 'en' ? 'English' : 'ગુજરાતી'}
                    </span>
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </button>

                {/* Dropdown Options - Hidden by default */}
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="py-1">
                      <button
                        onClick={() => handleLanguageChange('en')}
                        className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors duration-200 flex items-center gap-3 ${selectedLanguage === 'en' ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500' : 'text-slate-800'
                          }`}
                      >
                        <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                          <span className="text-xs font-medium text-white">EN</span>
                        </div>
                        <div>
                          <div className="font-medium">English</div>
                          <div className="text-xs text-slate-500">English language</div>
                        </div>
                        {selectedLanguage === 'en' && (
                          <svg className="w-5 h-5 text-blue-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>

                      <button
                        onClick={() => handleLanguageChange('gu')}
                        className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors duration-200 flex items-center gap-3 ${selectedLanguage === 'gu' ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500' : 'text-slate-800'
                          }`}
                      >
                        <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                          <span className="text-xs font-medium text-white">GU</span>
                        </div>
                        <div>
                          <div className="font-medium">ગુજરાતી</div>
                          <div className="text-xs text-slate-500">ગુજરાતી ભાષા</div>
                        </div>
                        {selectedLanguage === 'gu' && (
                          <svg className="w-5 h-5 text-blue-600 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/20 rounded-2xl p-6 shadow-sm border border-slate-200/60 hover:shadow-lg hover:border-slate-300/80 transition-all duration-500 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">{t("notifications.title")}</h3>
                <p className="text-sm text-slate-600">{t("notifications.description")}</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Email Notifications */}
              <div className="flex items-center justify-between p-3 bg-white/60 rounded-lg border border-slate-200/40">
                <div>
                  <h4 className="text-sm font-medium text-slate-800">{t("notifications.email.title")}</h4>
                  <p className="text-xs text-slate-600">{t("notifications.email.description")}</p>
                </div>
                <button
                  onClick={() => setNotifications(prev => ({ ...prev, email: !prev.email }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${notifications?.email ? 'bg-gradient-to-r from-emerald-500 to-teal-600 shadow-md' : 'bg-slate-200 hover:bg-slate-300'
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 shadow-sm ${notifications?.email ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>

              {/* SMS Notifications */}
              <div className="flex items-center justify-between p-3 bg-white/60 rounded-lg border border-slate-200/40">
                <div>
                  <h4 className="text-sm font-medium text-slate-800">{t("notifications.sms.title")}</h4>
                  <p className="text-xs text-slate-600">{t("notifications.sms.description")}</p>
                </div>
                <button
                  onClick={() => setNotifications(prev => ({ ...prev, sms: !prev.sms }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${notifications?.sms ? 'bg-gradient-to-r from-emerald-500 to-teal-600 shadow-md' : 'bg-slate-200 hover:bg-slate-300'
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-300 shadow-sm ${notifications?.sms ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Theme Settings */}
          {/* <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">Appearance</h3>
                                <p className="text-sm text-gray-600">Customize your app appearance</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-medium text-gray-800">Dark Mode</h4>
                                    <p className="text-xs text-gray-600">Switch to dark theme</p>
                                </div>
                                <button
                                    onClick={() => setDarkMode(!darkMode)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${darkMode ? 'bg-primary' : 'bg-gray-200'
                                        }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${darkMode ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>
                    </div> */}

        </div>


        {/* Action Buttons */}
        {/* <div className="mt-8 flex gap-4 justify-end">
                    <button className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all duration-300">
                        Reset to Default
                    </button> 
                    <button className="px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]"
                        onClick={handleSave}>
                        {t("save_settings")}
                    </button>
                </div> */}
      </div>
    </div>
  );
};

export default Setting;


{/* <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
              <SettingIcon />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{t('settings')}</h1>
              <p className="text-gray-600">{t('manage_your_application_preferences')}</p>
            </div>
          </div>
        </div> */}
